import { Controller, useForm } from 'react-hook-form';
import PermissionMenu from '../PermissionMenu/PermissionMenu';
import Button from 'components/elements/Button/Button';
import TextInput from 'components/elements/TextInput';
import Textarea from 'components/elements/Textarea';
import type { Box } from 'models/Box';
import type { InvitationRole } from 'models/Invitation';
import useCreateBoxInvitation from 'networking/mindfeed/hooks/useCreateBoxInvitation';
import { useApiValidationErrors } from 'networking/mindfeed/hooks/useApiValidationErrors';
import { openBoxModal } from 'xstate/actors/ModalActor';
import { BoxModalStep } from 'xstate/machines/ModalMachine';
import { useTranslations } from 'next-intl';

interface InvitePeopleFormProps {
  box: Box;
}

interface InviteFormValues {
  destination: string;
  message: string;
  role: InvitationRole;
}

const InvitePeopleForm = ({ box }: InvitePeopleFormProps) => {
  const t = useTranslations();
  const createInvitationMutation = useCreateBoxInvitation();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    control,
    formState: { errors },
    reset,
  } = useForm<InviteFormValues>({
    defaultValues: {
      destination: '',
      message: '',
      role: 'editor',
    },
  });

  const { handleApiError } = useApiValidationErrors({ setError });

  const isSending = createInvitationMutation.isPending;
  const destination = watch('destination');
  const canSend = destination.trim().length > 0 && !isSending;

  const onSubmit = async (values: InviteFormValues) => {
    clearErrors();

    try {
      await createInvitationMutation.mutateAsync({
        boxId: box.id,
        payload: {
          destination: values.destination.trim(),
          message: values.message.trim() || undefined,
          method: 'email',
          role: values.role,
        },
      });

      reset();

      openBoxModal(box, {
        step: BoxModalStep.ManagePermissions,
        canNavigateBack: true,
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.row}>
        <TextInput
          label={t('boxSharing.email')}
          type="email"
          placeholder={t('boxSharing.email')}
          hideLabel
          tabIndex={1}
          className={styles.emailInput}
          errors={errors}
          {...register('destination', {
            required: t('boxSharing.emailRequired'),
          })}
        />

        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <PermissionMenu
              permission={field.value === 'viewer' ? 'view' : 'edit'}
              onChange={(p) =>
                field.onChange(p === 'view' ? 'viewer' : 'editor')
              }
              showRemove={false}
              showChevronUpWhenOpen
            />
          )}
        />
      </div>

      <Textarea
        label={t('boxSharing.message')}
        placeholder={t('boxSharing.messagePlaceholder')}
        hideLabel
        tabIndex={2}
        className={styles.messageBox}
        minRows={4}
        errors={errors}
        {...register('message')}
      />

      <div className={styles.footer}>
        <Button
          type="submit"
          size="sm"
          disabled={!canSend}
          loading={isSending}
          loadingText={t('common.sending')}
        >
          {t('boxSharing.sendInvite')}
        </Button>
      </div>
    </form>
  );
};

const styles = {
  container: 'bg-white px-6',
  row: 'flex items-center gap-3',
  emailInput: 'w-full',
  messageBox: 'mt-6 min-h-[120px]',
  footer: 'mt-6 flex justify-end',
};

export default InvitePeopleForm;
